import { useEffect, useState, useRef, useCallback } from 'react';
import { growthMetrics } from '@/mocks/growthCenter';
import { sponsorMetrics } from '@/mocks/sponsors';
import { testimonials } from '@/mocks/testimonials';

/* ── Animated Counter ── */
function AnimatedCounter({ value, suffix, prefix, decimals }: { value: number; suffix: string; prefix?: string; decimals?: number }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !hasAnimated) setHasAnimated(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasAnimated]);

  useEffect(() => {
    if (!hasAnimated) return;
    const duration = 1800;
    const steps = 50;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) { setCount(value); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [hasAnimated, value]);

  const formatted = decimals
    ? (count / Math.pow(10, decimals)).toFixed(decimals)
    : count >= 1000 ? `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}K` : `${count}`;

  return (
    <span ref={ref} className="tabular-nums">
      {prefix || ''}{formatted}{suffix}
    </span>
  );
}

/* ── Growth KPI data ── */
const growthKpis = [
  {
    icon: 'ri-radio-line',
    label: 'Radios activas',
    value: growthMetrics.totalRadiosTracked,
    suffix: '',
    trend: `+${growthMetrics.growingFastCount} en crecimiento rápido`,
    trendUp: true,
    color: 'emerald',
  },
  {
    icon: 'ri-headphone-line',
    label: 'Oyentes nuevos',
    value: growthMetrics.totalNewListeners,
    suffix: '+',
    trend: '+12.4% crecimiento promedio',
    trendUp: true,
    color: 'amber',
  },
  {
    icon: 'ri-hand-coin-line',
    label: 'Patrocinadores',
    value: sponsorMetrics.activeSponsors,
    suffix: '',
    trend: `$${(sponsorMetrics.totalInvestment / 1000000).toFixed(1)}M inversión`,
    trendUp: true,
    color: 'orange',
  },
  {
    icon: 'ri-user-heart-line',
    label: 'Seguidores nuevos',
    value: growthMetrics.totalNewFollowers,
    suffix: '+',
    trend: 'en todas las radios',
    trendUp: true,
    color: 'rose',
  },
];

const colorMap: Record<string, { iconBg: string; iconColor: string; trendColor: string; cardHover: string }> = {
  emerald: { iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-600', trendColor: 'text-emerald-600', cardHover: 'hover:border-emerald-500/25 hover:shadow-emerald-500/5' },
  amber:   { iconBg: 'bg-amber-500/10', iconColor: 'text-amber-600', trendColor: 'text-amber-600', cardHover: 'hover:border-amber-500/25 hover:shadow-amber-500/5' },
  orange:  { iconBg: 'bg-orange-500/10', iconColor: 'text-orange-600', trendColor: 'text-orange-600', cardHover: 'hover:border-orange-500/25 hover:shadow-orange-500/5' },
  rose:    { iconBg: 'bg-rose-500/10', iconColor: 'text-rose-600', trendColor: 'text-rose-600', cardHover: 'hover:border-rose-500/25 hover:shadow-rose-500/5' },
};

/* ── Selected testimonials for the grid ── */
const featuredTestimonials = testimonials.filter(t => t.rating >= 4).slice(0, 6);

export default function TestimoniosCrecimientoSection() {
  const [active, setActive] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const next = useCallback(() => {
    setActive((prev) => (prev + 1) % featuredTestimonials.length);
  }, []);

  const prev = useCallback(() => {
    setActive((prev) => (prev - 1 + featuredTestimonials.length) % featuredTestimonials.length);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(next, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, next]);

  const t = featuredTestimonials[active];

  return (
    <section id="testimonios-crecimiento" className="relative py-20 md:py-28 overflow-hidden bg-background-50">
      {/* Ambient glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-accent-500/2 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-[350px] h-[350px] bg-emerald-500/2 rounded-full blur-3xl pointer-events-none"></div>

      {/* Subtle dot grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.015]" style={{
        backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
        backgroundSize: '20px 20px',
      }}></div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* ── Header ── */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-100/80 border border-rose-200/70 text-rose-700 text-xs font-semibold mb-5">
            <i className="ri-verified-badge-line text-xs"></i>
            Prueba social
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tight leading-[1.1]">
            Radios que <span className="text-rose-600">crecen</span>, comunidades que{' '}
            <span className="text-rose-600">confían</span>
          </h2>
          <p className="mt-4 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Miles de radios, locutores, oyentes y patrocinadores ya son parte del ecosistema social RFW. Estos son los números reales de nuestra comunidad.
          </p>
        </div>

        {/* ── Growth KPIs Grid ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-16">
          {growthKpis.map((kpi) => {
            const c = colorMap[kpi.color];
            return (
              <div
                key={kpi.label}
                className={`group relative rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 ${c.cardHover}`}
              >
                {/* Icon */}
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${c.iconBg} ${c.iconColor} mb-4 transition-all duration-300 group-hover:scale-110`}>
                  <i className={`${kpi.icon} text-lg`}></i>
                </div>

                {/* Value */}
                <div className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 mb-1">
                  <AnimatedCounter value={kpi.value} suffix={kpi.suffix} />
                </div>

                {/* Label */}
                <p className="text-foreground-500 text-xs font-medium mb-2">{kpi.label}</p>

                {/* Trend */}
                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${kpi.trendUp ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
                  <span className={`text-[10px] font-semibold ${c.trendColor}`}>{kpi.trend}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Testimonial Carousel ── */}
        <div
          className="relative max-w-3xl mx-auto bg-background-100 rounded-3xl border border-background-200/70 p-8 md:p-10"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          {/* Quote icon */}
          <div className="absolute -top-5 -left-5 w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center">
            <i className="ri-double-quotes-l text-background-50 text-lg"></i>
          </div>

          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Avatar + info */}
            <div className="flex flex-col items-center gap-3 shrink-0">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden bg-background-200 border-2 border-background-200">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                <p className="text-[10px] text-foreground-500 mt-0.5">{t.role}</p>
                {t.stationName && (
                  <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-md bg-rose-100/80 text-rose-700 text-[9px] font-semibold">
                    <i className="ri-radio-line text-[9px]"></i>
                    {t.stationName}
                  </span>
                )}
              </div>
              {/* Stars */}
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`${i < t.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'} text-sm`}
                  ></i>
                ))}
              </div>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base italic">
                &ldquo;{t.text}&rdquo;
              </p>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-background-200/70">
            <div className="flex items-center gap-2">
              {featuredTestimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setActive(i); setIsAutoPlaying(false); }}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${i === active ? 'w-8 h-2 bg-rose-500' : 'w-2 h-2 bg-foreground-300 hover:bg-foreground-400'}`}
                ></button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={prev}
                className="w-9 h-9 rounded-full bg-background-200 border border-background-300/60 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
              >
                <i className="ri-arrow-left-line text-foreground-600 text-sm"></i>
              </button>
              <button
                onClick={next}
                className="w-9 h-9 rounded-full bg-rose-500 flex items-center justify-center cursor-pointer hover:bg-rose-600 transition-colors"
              >
                <i className="ri-arrow-right-line text-background-50 text-sm"></i>
              </button>
            </div>
          </div>
        </div>

        {/* ── Mini grid of all testimonials ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-6 max-w-3xl mx-auto">
          {featuredTestimonials.map((item, i) => (
            <button
              key={item.id}
              onClick={() => { setActive(i); setIsAutoPlaying(false); }}
              className={`text-left p-3 rounded-xl border transition-all duration-200 cursor-pointer ${i === active ? 'bg-rose-50 border-rose-300 ring-1 ring-rose-200' : 'bg-background-100 border-background-200 hover:border-rose-200'}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg overflow-hidden bg-background-200 shrink-0">
                  <img
                    src={item.avatar}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold text-foreground-800 truncate">{item.name}</p>
                  <p className="text-[9px] text-foreground-400 truncate">
                    {item.category === 'oyente' ? 'Oyente' : item.category === 'locutor' ? 'Locutor' : 'Dueño'}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* ── Trust strip ── */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-12">
          {[
            { icon: 'ri-shield-check-line', text: 'Métricas verificadas en tiempo real' },
            { icon: 'ri-group-line', text: '+68 radios activas en el ecosistema' },
            { icon: 'ri-flashlight-line', text: 'Comunidad en crecimiento acelerado' },
          ].map((pill) => (
            <span
              key={pill.text}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-100/80 border border-rose-200/70 text-rose-700 text-[11px] font-semibold whitespace-nowrap"
            >
              <i className={`${pill.icon} text-xs`}></i>
              {pill.text}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}